import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRow,
} from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { COLOR_PRIMARY_2, COLOR_SECONDARY_1, COLOR_PRIMARY_1 } from 'src/colors';
import { AFFILIATE_COMMISSION_IN_USD, COLOR_RED } from 'src/const';

export interface DailyData {
  date: string;
  visitors_count: number;
  signup_count: number;
  subscription_count: number;
  signup_emails: string[];
  subscribed_emails: string[];
  daily_subscription_prices?: { email: string; payment_price: number | null }[];
}

interface DailyPerformanceTableProps {
  data: DailyData[];
}

const DailyPerformanceTable: React.FC<DailyPerformanceTableProps> = ({ data }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleRowExpand = (id: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Transform data for DataGrid
  const rows = data.map((item, index) => ({
    id: item.date,
    date: new Date(item.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }),
    visitors: item.visitors_count,
    signups: item.signup_count,
    sales: item.subscription_count,
    commission: (
      item.daily_subscription_prices
        ? item.daily_subscription_prices
            .filter(sub => sub.payment_price !== null)
            .reduce((sum, sub) => sum + (sub.payment_price || 0), 0)
            .toFixed(2)
        : '0.00'
    ),
    signup_emails: item.signup_emails,
    subscribed_emails: item.subscribed_emails,
    daily_subscription_prices: item.daily_subscription_prices,
  }));

  const DetailRow: React.FC<{ rowData: any }> = ({ rowData }) => {
    return (
      <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          📊 Row Details (click <ExpandLessIcon sx={{ fontSize: 18, verticalAlign: 'middle' }} /> to collapse):
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Signups Section */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: COLOR_SECONDARY_1 }}>
              Signups:
            </Typography>
            {rowData.signup_emails && rowData.signup_emails.length > 0 ? (
              <List dense sx={{ mt: 1 }}>
                {rowData.signup_emails.map((email: string, index: number) => (
                  <ListItem key={index} sx={{ py: 0.5, pl: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: COLOR_PRIMARY_2,
                        fontSize: '0.875rem',
                        '&:before': {
                          content: '"•"',
                          marginRight: '8px',
                          color: COLOR_SECONDARY_1
                        }
                      }}
                    >
                      {email}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No signups for this day
              </Typography>
            )}
          </Box>

          {/* Sales Section */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: COLOR_PRIMARY_1 }}>
              Sales:
            </Typography>
            {rowData.daily_subscription_prices && rowData.daily_subscription_prices.length > 0 ? (
              <List dense sx={{ mt: 1 }}>
                {rowData.daily_subscription_prices.map((item: { email: string; payment_price: number | null }, index: number) => (
                  <ListItem key={index} sx={{ py: 0.5, pl: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLOR_PRIMARY_2,
                        fontSize: '0.875rem',
                        '&:before': {
                          content: '"•"',
                          marginRight: '8px',
                          color: COLOR_PRIMARY_1,
                        },
                      }}
                    >
                      {item.email}
                    </Typography>
                    <Chip
                      label={item.payment_price !== null ? `$${item.payment_price.toFixed(2)}` : 'Error'}
                      size="small"
                      sx={{
                        backgroundColor: item.payment_price !== null ? COLOR_PRIMARY_2 : COLOR_RED,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No sales for this day
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const CustomGridRow = (props: any) => {
    const { row, children } = props;
    const isExpanded = expandedRows.has(row.id);

    return (
      <Box
        sx={{
          backgroundColor: isExpanded ? '#f0f0f0' : 'inherit',
        }}
      >
        <GridRow {...props} />
        <Collapse in={isExpanded}>
          <DetailRow rowData={row} />
        </Collapse>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      // width: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'visitors',
      headerName: 'Visitors',
      // width: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'signups',
      headerName: 'Signups',
      // width: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'sales',
      headerName: 'Sales',
      // width: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'commission',
      headerName: 'Commission Earned',
      // width: 150,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          ${params.value}
        </Typography>
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      // width: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={() => handleRowExpand(params.row.id)}
          sx={{
            backgroundColor: COLOR_PRIMARY_2,
            color: 'white',
            width: 24,
            height: 24,
            '&:hover': {
              backgroundColor: COLOR_PRIMARY_1,
            },
          }}
        >
          {expandedRows.has(params.row.id) ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ borderRadius: '8px', overflow: 'hidden', height: '500px', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter
          disableSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: `2px solid ${COLOR_PRIMARY_2}`,
              '& .MuiDataGrid-columnHeader': {
                fontWeight: 600,
                color: COLOR_PRIMARY_2,
              },
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f5f7fa',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid #e0e0e0`,
              fontSize: '0.875rem',
            },
          }}
          getRowHeight={(params) => 
            expandedRows.has(params.id as string) ? 'auto' : 52
          }
          components={{ Row: CustomGridRow }}
        />
      </Paper>
    </Box>
  );
};

export default DailyPerformanceTable; 